import React, { useEffect, useMemo, useState } from 'react';

const formatBusinessAddress = (business) => {
  if (business.address) return business.address;
  const parts = [
    business.addressLine1,
    business.addressLine2,
    business.addressLine3,
    business.postCode,
  ].filter(Boolean);
  return parts.join(', ');
};

const StepBusinessLookup = ({
  formData,
  updateFormData,
  searchFhrsBusiness,
  errors,
}) => {
  const [searchName, setSearchName] = useState('');
  const [searchPostcode, setSearchPostcode] = useState(formData.homeAddress?.postcode || '');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [lookupError, setLookupError] = useState(null);

  const existingSelection = useMemo(() => {
    if (!formData.fhrsBusinessId) return null;
    return {
      businessName: formData.fhrsBusinessName || 'Your registered business',
      ratingValue:
        formData.fhrsRating === -1 ? 'AwaitingInspection' : formData.fhrsRating,
      ratingDate: formData.fhrsRatingDate,
      address: formData.fhrsBusinessAddress || '',
      ratingImageUrl: formData.fhrsRatingImageUrl || null,
    };
  }, [
    formData.fhrsBusinessAddress,
    formData.fhrsBusinessId,
    formData.fhrsRating,
    formData.fhrsRatingDate,
    formData.fhrsBusinessName,
    formData.fhrsRatingImageUrl
  ]);

  useEffect(() => {
    if (existingSelection && !selectedBusiness) {
      setSelectedBusiness(existingSelection);
    }
  }, [existingSelection, selectedBusiness]);

  const handleSearch = async () => {
    if (!searchName.trim() || !searchPostcode.trim()) {
      setLookupError('Enter both a business name and postcode to search FHRS.');
      return;
    }

    setSearching(true);
    setLookupError(null);
    setSearchResults(null);
    setSelectedBusiness(null);

    try {
      const result = await searchFhrsBusiness(searchName.trim(), searchPostcode.trim());
      if (result.success) {
        setSearchResults(result.data);
        if (!result.data?.businesses?.length) {
          setLookupError(
            'No FHRS records found. You can still continue, just let us know if you need help registering.'
          );
        }
      } else {
        setLookupError(result.error || 'Search failed. Please try again.');
      }
    } catch (error) {
      setLookupError('Search failed. Please try again in a moment.');
    } finally {
      setSearching(false);
    }
  };

  const normalizeRating = (ratingValue) => {
    if (ratingValue === 'AwaitingInspection') {
      return -1;
    }
    const numeric = Number(ratingValue);
    return Number.isNaN(numeric) ? null : numeric;
  };

  const handleBusinessSelect = (business) => {
    const rating = normalizeRating(business.ratingValue);
    const address = formatBusinessAddress(business);

    setSelectedBusiness({
      businessName: business.businessName,
      ratingValue: business.ratingValue,
      ratingDate: business.ratingDate,
      address,
      ratingImageUrl: business.ratingImageUrl || null,
    });

    updateFormData({
      fhrsBusinessId: business.fhrsId || business.FHRSID || null,
      fhrsBusinessName: business.businessName,
      fhrsBusinessAddress: address,
      fhrsRating: rating,
      fhrsRatingImageUrl: business.ratingImageUrl || '',
      fhrsRatingDate: business.ratingDate || null,
      registrationStatus: rating === -1 ? 'awaiting_inspection' : 'registered',
      needsRegistrationSupport: false,
      willingToImprove:
        rating !== null && rating >= 3
          ? true
          : formData.willingToImprove,
    });
  };

  const handleWillingToImprove = (willing) => {
    updateFormData({
      willingToImprove: willing,
      registrationStatus: willing ? 'awaiting_inspection' : 'not_registered',
    });
  };

  const handleNeedSupport = () => {
    setSelectedBusiness(null);
    updateFormData({
      fhrsBusinessId: null,
      fhrsBusinessName: null,
      fhrsBusinessAddress: null,
      fhrsRating: null,
      fhrsRatingDate: null,
      registrationStatus: 'not_registered',
      needsRegistrationSupport: true,
    });
  };

  const hasLowRating =
    selectedBusiness &&
    selectedBusiness.ratingValue !== 'AwaitingInspection' &&
    Number(selectedBusiness.ratingValue) < 3;

  return (
    <div className="space-y-8">
      <div className="bg-white border border-[#F0F0F0] rounded-[20px] p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-body-md font-medium text-text-primary mb-2">
              Trading name
            </label>
            <input
              type="text"
              placeholder="e.g. Nana Ama's Kitchen"
              value={searchName}
              onChange={(event) => setSearchName(event.target.value)}
              className="w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid border-shell-border outline-none focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20 placeholder:text-[#A3A3A3] text-body-md transition-all"
            />
          </div>
          <div>
            <label className="block text-body-md font-medium text-text-primary mb-2">
              Postcode
            </label>
            <input
              type="text"
              placeholder="SW1A 1AA"
              value={searchPostcode}
              onChange={(event) => setSearchPostcode(event.target.value)}
              className="w-full px-4 h-[54px] rounded-[20px] text-text-primary bg-[#FAFAFA] border-2 border-solid border-shell-border outline-none focus:border-accent-blue focus:ring-4 focus:ring-aurora-primary/20 placeholder:text-[#A3A3A3] text-body-md uppercase transition-all"
            />
          </div>
        </div>
        <button
          type="button"
          className={`w-full text-center py-4 rounded-xl font-semibold text-white bg-gradient-to-r from-aurora-primary to-accent-blue-dark hover:shadow-xl hover:scale-105 transition-all duration-300 ${searching ? 'opacity-75 cursor-wait' : ''}`}
          onClick={handleSearch}
          disabled={searching}
        >
          {searching ? 'Searching FHRS…' : 'Search FHRS database'}
        </button>
        {lookupError && (
          <p className="text-body-sm text-red-600">{lookupError}</p>
        )}
      </div>

      {searchResults?.businesses?.length > 0 && (
        <div className="bg-white border border-[#F0F0F0] rounded-[20px] p-6 space-y-4">
          <h4 className="text-body-lg font-semibold text-text-primary">
            Select your registration
          </h4>
          <div className="space-y-3">
            {searchResults.businesses.map((business) => {
              const address = formatBusinessAddress(business);
              const isSelected =
                selectedBusiness?.businessName === business.businessName &&
                selectedBusiness?.address === address;
              return (
                <button
                  key={business.fhrsId || business.FHRSID}
                  type="button"
                  onClick={() => handleBusinessSelect(business)}
                  className={`w-full text-left p-4 rounded-[16px] border-2 transition-all duration-200 ${
                    isSelected
                      ? 'border-accent-blue bg-aurora-bg-secondary'
                      : 'border-shell-border hover:border-accent-blue hover:shadow-md'
                  }`}
                >
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                      <p className="text-body-lg font-semibold text-text-primary">
                        {business.businessName}
                      </p>
                      <p className="text-body-sm text-text-primary-secondary mt-1">{address}</p>
                      <p className="text-body-sm text-text-primary-secondary mt-1">
                        FHRS rating: {business.ratingValue}
                      </p>
                    </div>
                    {business.ratingImageUrl && (
                      <img
                        src={business.ratingImageUrl}
                        alt={`FHRS rating ${business.ratingValue}`}
                        className="w-24 h-auto rounded-md border border-[#ECECEC] bg-white p-1"
                        loading="lazy"
                      />
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedBusiness && (
        <div className="bg-[#FAFAFA] border border-[#F0F0F0] rounded-[20px] p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h4 className="text-body-lg font-semibold text-text-primary">
                {selectedBusiness.businessName}
              </h4>
              <p className="text-body-sm text-text-primary-secondary mt-1">
                {selectedBusiness.address}
              </p>
              <p className="text-body-sm text-text-primary-secondary mt-2">
                FHRS rating:{' '}
                {selectedBusiness.ratingValue === 'AwaitingInspection'
                  ? 'Awaiting first inspection'
                  : selectedBusiness.ratingValue}
              </p>
              {selectedBusiness.ratingDate && (
                <p className="text-body-xs text-text-primary-secondary mt-1">
                  Last updated {new Date(selectedBusiness.ratingDate).toLocaleDateString('en-GB')}
                </p>
              )}
            </div>
            {selectedBusiness.ratingImageUrl && (
              <img
                src={selectedBusiness.ratingImageUrl}
                alt={`FHRS rating ${selectedBusiness.ratingValue}`}
                className="w-28 h-auto rounded-md border border-[#ECECEC] bg-white p-2"
                loading="lazy"
              />
            )}
            {hasLowRating && (
              <div className="bg-white border border-[#FFE7AC] rounded-[16px] p-4 w-full md:w-auto">
                <p className="text-body-sm text-text-primary mb-3">
                  Your rating is below 3. Are you ready to work with Kinn on a hygiene plan?
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    className="btn-primary flex-1"
                    onClick={() => handleWillingToImprove(true)}
                  >
                    Yes, let’s improve it
                  </button>
                  <button
                    type="button"
                    className="btn-secondary flex-1"
                    onClick={() => handleWillingToImprove(false)}
                  >
                    Not right now
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {(!searchResults?.businesses?.length || formData.registrationStatus === 'not_registered') && (
        <div className="bg-[#FFF7E6] border border-[#FEC51C] rounded-[16px] p-5 space-y-3">
          <h4 className="text-body-md font-semibold text-text-primary">
            Can’t find your registration?
          </h4>
          <p className="text-body-sm text-text-primary/80">
            That’s okay. We’ll walk you through food business registration and hygiene prep.
          </p>
          <button
            type="button"
            className="btn-secondary"
            onClick={handleNeedSupport}
          >
            I need help registering
          </button>
        </div>
      )}

      {(errors.registration || errors.willingToImprove) && (
        <div className="bg-red-50 border border-red-200 rounded-[16px] p-4 space-y-2">
          {errors.registration && (
            <p className="text-body-sm text-red-700">{errors.registration}</p>
          )}
          {errors.willingToImprove && (
            <p className="text-body-sm text-red-700">{errors.willingToImprove}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default StepBusinessLookup;
